import { PermissionTypes, ShareUserViewModel } from '@dntlab/share-files-dialog';
import FileSystemItem from 'devextreme/file_management/file_system_item';
import AppSettings from 'src/app/common/app.settings';
import { FileFromManager, FileHistory } from 'src/app/interfaces/file.interface';
import {
  AccountStatus,
  ConnectionStatus,
  FilePermissions,
  FileSizeResponse,
  FolderDescriptor,
  FoldersResponse,
  FoldersResponseInner,
  GenerateFileResponse,
  ListOfBasicUsers,
  SecurityDescriptor,
  UserBasic,
  UserExtended,
  UserRole,
} from 'src/app/swagger-api';
import { Repeat } from 'src/app/utils';
import EntityKindEnum = FoldersResponseInner.EntityKindEnum;

export interface FileMockOptionsInterface {
  security?: SecurityDescriptor[];
  contentType?: string;
  name?: string;
  isPublic?: boolean;
  publicUrl?: string;
  user_id?: string;
}

const userId = 'c1fff5e847874c75bc6ce4994b1d81d6';

export const allExtendedUsersMock: Repeat<4, UserExtended> = [
  {
    user_id: userId,
    displayed_name: 'mark.zuckerberg',
    is_admin: false,
    account_status: AccountStatus.ACTIVE,
    image_url: '',
    reaction: {
      blocked: false,
      connection_details: {
        status: ConnectionStatus.Verified,
        initiator_id: userId,
      },
    },
    chat: `fake_chat_unique_name_${userId}`,
    user_roles: [UserRole.Organization],
  },
  {
    user_id: 'userid2',
    displayed_name: 'evans.adams',
    is_admin: false,
    account_status: AccountStatus.ACTIVE,
    image_url: 'https://example.com/evans.adams',
    reaction: {
      blocked: false,
      connection_details: {
        status: ConnectionStatus.Verified,
        initiator_id: 'userid2',
      },
    },
    chat: `fake_chat_unique_name_userid2`,
    user_roles: [UserRole.Organization],
  },
  {
    user_id: 'userid3',
    displayed_name: 'gilbert',
    is_admin: false,
    account_status: AccountStatus.ACTIVE,
    image_url: '',
    reaction: {
      blocked: true,
      connection_details: {
        status: ConnectionStatus.Verified,
        initiator_id: 'userid3',
      },
    },
    chat: `fake_chat_unique_name_userid3`,
    user_roles: [UserRole.Organization],
  },
  {
    user_id: 'userid4',
    displayed_name: 'selena.ham',
    is_admin: false,
    account_status: AccountStatus.DEACTIVATED,
    image_url: '',
    reaction: {
      blocked: false,
      connection_details: {
        status: ConnectionStatus.Verified,
        initiator_id: 'userid4',
      },
    },
    chat: `fake_chat_unique_name_userid4`,
    user_roles: [UserRole.Organization],
  },
];

export const allBasicUsersMock: ListOfBasicUsers = {
  items: allExtendedUsersMock.map((userBasic: UserExtended): UserBasic => {
    return {
      user_id: userBasic.user_id,
      displayed_name: userBasic.displayed_name,
      is_admin: userBasic.is_admin,
      account_status: AccountStatus.ACTIVE,
      image_url: userBasic.image_url,
      chat: userBasic.chat,
    };
  }),
};

export const fileUser: ShareUserViewModel = {
  id: allExtendedUsersMock[0].user_id,
  displayName: allExtendedUsersMock[0].displayed_name,
  email: '',
  imageUrl: AppSettings.NO_PHOTO_DEFAULT_IMAGE,
  userStatus: AccountStatus.ACTIVE,
};

export const fileMock: FoldersResponseInner = {
  entity: {
    content_type: 'audio/wav',
    created: '2020-06-17',
    creator_account_id: userId,
    entity_id: '84a4c964cc674da484ffb765b54cd6ca',
    modified: '2020-06-17',
    name: 'Test audio.wav',
    is_public: false,
    public_url: 'https://example.com/test_audio.wav',
    size: 97872898,
    state: 'pending',
  },
  entity_kind: 'file_descriptor',
  security: [{ user_id: userId, permission: 'owner' }],
};

export function getFile(file?: FileMockOptionsInterface): FoldersResponseInner {
  const security: SecurityDescriptor[] = file.security || [{ user_id: userId, permission: FilePermissions.Owner }];
  const contentType: string = file.contentType || 'video/mp4';
  const name: string = file.name || 'test video.mp4';
  const isPublic: boolean = file.isPublic || false;
  const publicUrl: string = file.publicUrl || '';
  const createAccountId: string = file.user_id || userId;

  return {
    entity: {
      content_type: contentType,
      created: '2020-06-17',
      creator_account_id: createAccountId,
      entity_id: '32bc908cb7a8498193d9c354cd6ca',
      modified: '2020-06-17',
      name,
      is_public: isPublic,
      public_url: publicUrl,
      size: 97872898,
      state: 'pending',
    },
    entity_kind: 'file_descriptor',
    security,
  };
}

export const filesMock: Repeat<2, FoldersResponseInner> = [
  structuredClone(fileMock),
  {
    entity: {
      content_type: 'video/mp4',
      created: '2020-06-17',
      creator_account_id: userId,
      entity_id: '32bc908cb7a8498193d9c354cd6ca',
      modified: '2020-06-17',
      name: 'test video.mp4',
      is_public: false,
      public_url: 'https://example.com/test_video.mp4',
      size: 97872898,
      state: 'pending',
    },
    entity_kind: 'file_descriptor',
    security: [{ user_id: userId, permission: 'owner' }],
  },
];

export const allTypesFilesMock: FoldersResponseInner[] = structuredClone(filesMock).concat([
  {
    entity: {
      content_type: 'application/pdf',
      created: '2020-06-17',
      creator_account_id: userId,
      entity_id: '32bc908cb7a8498193d9c354cd6ca',
      modified: '2020-06-17',
      name: 'Test document.pdf',
      is_public: false,
      public_url: 'https://example.com/test_document.pdf',
      size: 97872898,
      state: 'pending',
    },
    entity_kind: 'file_descriptor',
    security: [{ user_id: userId, permission: 'owner' }],
  },
  {
    entity: {
      content_type: 'image/png',
      created: '2020-06-17',
      creator_account_id: userId,
      entity_id: '32bc908cb7a8498193d9c354cd6ca',
      modified: '2020-06-17',
      name: 'Test image.png',
      is_public: false,
      size: 97872898,
      state: 'pending',
    },
    entity_kind: 'file_descriptor',
    security: [{ user_id: allExtendedUsersMock[1].user_id, permission: FilePermissions.Reader }],
  },
]);

export const fileHistoryMock: FileHistory = {
  items: [
    {
      modified_date: 'Mon Sep 29 2019 15:41:57',
      event_type: 'uploaded',
      modified_by: {
        user_id: 'userid4',
        displayed_name: 'Smith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
    },
  ],
};

export const fileStorageSizeMock: FileSizeResponse = {
  loading_size: 711460786,
  shared_size: 31371749,
  total_size: 1073741824,
  used_size: 742832535,
};

export const fileStorageSizeUpdatedMock: FileSizeResponse = {
  loading_size: 711460786,
  shared_size: 31371749,
  total_size: 1073741824,
  used_size: 742832599,
};

export const generateLinkResponse: GenerateFileResponse = {
  file_descriptor_id: 'fakeid',
  url: 'https://www.example.com/fakeurl',
  fields: ['test1'],
};
export const foldersResponse: FoldersResponse = [
  {
    entity: {
      created: '2020-17-06',
      creator_account_id: userId,
      entity_id: '32bc908cb7a8498193d9c354cd6ca',
      modified: '2020-17-06',
      name: 'Test folder',
      modified_by: userId,
      readonly: false,
    } as FolderDescriptor,
    entity_kind: EntityKindEnum.FolderDescriptor,
    security: [{ user_id: userId, permission: 'owner' }],
  },
];

export const fileSystemItem: FileSystemItem = {
  name: 'November 2022. Monthly plans for teams..docx',
  key: '78dfdcf349bf4675892fc6d11aa8a14c',
  path: 'November 2022. Monthly plans for teams..docx',
  pathKeys: ['78dfdcf349bf4675892fc6d11aa8a14c'],
  isDirectory: false,
  hasSubDirectories: false,
  size: 933533,
  dateModified: new Date('2022-11-11T22:00:00.000Z'),
  thumbnail: '',
  getFileExtension: () => 'docx',
  dataItem: {
    name: 'November 2022. Monthly plans for teams..docx',
    isDirectory: false,
    size: 933533,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '78dfdcf349bf4675892fc6d11aa8a14c',
          name: 'November 2022. Monthly plans for teams..docx',
          creator_account_id: '50a2d93e803241df8dd64994364e3853',
          content_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          size: 933533,
          state: 'pending',
          created: '11/12/2022',
          is_public: false,
          modified: '11/12/2022',
          age_in_folder: '2022-11-12T14:58:54-08:00',
          public_url: '',
        },
        security: [{ permission: 'owner', user_id: '50a2d93e803241df8dd64994364e3853' }],
      },
      members: 'Only you',
    },
    dateModified: '11/12/2022',
    hasSubDirectories: false,
    path: '',
    thumbnail: '',
    key: '78dfdcf349bf4675892fc6d11aa8a14c',
  },
};
export const uploadingFileMock: File = {
  lastModified: 1671708905096,
  size: 401158,
  type: 'image/png',
  name: 'test.png',
} as File;

export const mockFile: (name?: string) => File = (name = 'test.png') =>
  ({ name, size: 1024, type: 'image/png', lastModified: 452252 }) as File;

export function createMockFileSystemItem(creatorId: string, permissionType: PermissionTypes): FileSystemItem {
  return {
    dataItem: {
      dataItem: {
        item: {
          entity: { creator_account_id: creatorId },
          security: { permission: permissionType, user_id: creatorId },
        },
      },
    },
  } as FileSystemItem;
}

export const mockDocumentFile: FileSystemItem = {
  name: 'GC_Intermediate_2_Blue.pdf',
  key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  path: 'GC_Intermediate_2_Blue.pdf',
  pathKeys: ['8d6df1541a5b4c75a6cd4a4e1e47e061'],
  isDirectory: false,
  size: 773862,
  dateModified: new Date('2022-08-14T22:00:00.000Z'),
  thumbnail: '',
  hasSubDirectories: false,
  getFileExtension: () => 'docx',
  dataItem: {
    name: 'GC_Intermediate_2_Blue.pdf',
    isDirectory: false,
    size: 773862,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '8d6df1541a5b4c75a6cd4a4e1e47e061',
          name: 'GC_Intermediate_2_Blue.pdf',
          creator_account_id: '032595cf63f74799816efd8f35397247',
          content_type: 'application/pdf',
          size: 773862,
          state: 'pending',
          created: '08/15/2022',
          public: false,
          modified: '08/15/2022',
          age_in_folder: '2022-08-15T11:09:56-07:00',
          public_url: '',
        },
        security: [{ permission: 'owner', user_id: '032595cf63f74799816efd8f35397247' }],
      },
      members: 'Only you',
    },
    dateModified: '08/15/2022',
    path: '',
    thumbnail: '',
    key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  },
};

export const mockDocumentFileWithTwoUsers: FileSystemItem = {
  name: 'GC_Intermediate_2_Blue.pdf',
  key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  path: 'GC_Intermediate_2_Blue.pdf',
  pathKeys: ['8d6df1541a5b4c75a6cd4a4e1e47e061'],
  isDirectory: false,
  size: 773862,
  dateModified: new Date('2022-08-14T22:00:00.000Z'),
  thumbnail: '',
  hasSubDirectories: false,
  getFileExtension: () => 'docx',
  dataItem: {
    name: 'GC_Intermediate_2_Blue.pdf',
    isDirectory: false,
    size: 773862,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '8d6df1541a5b4c75a6cd4a4e1e47e061',
          name: 'GC_Intermediate_2_Blue.pdf',
          creator_account_id: '032595cf63f74799816efd8f35397247',
          content_type: 'application/pdf',
          size: 773862,
          state: 'pending',
          created: '08/15/2022',
          public: false,
          modified: '08/15/2022',
          age_in_folder: '2022-08-15T11:09:56-07:00',
          public_url: '',
        },
        security: [
          { permission: 'owner', user_id: '032595cf63f74799816efd8f35397247' },
          { permission: 'reader', user_id: '546456546y54hgg54h54h4fg73454g55' },
        ],
      },
      members: 'Only you',
    },
    dateModified: '08/15/2022',
    path: '',
    thumbnail: '',
    key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  },
};

export const mockDocumentFileWithTwoUsersOneOwner: FileSystemItem = {
  name: 'GC_Intermediate_2_Blue.pdf',
  key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  path: 'GC_Intermediate_2_Blue.pdf',
  pathKeys: ['8d6df1541a5b4c75a6cd4a4e1e47e061'],
  isDirectory: false,
  size: 773862,
  dateModified: new Date('2022-08-14T22:00:00.000Z'),
  thumbnail: '',
  hasSubDirectories: false,
  getFileExtension: () => 'docx',
  dataItem: {
    name: 'GC_Intermediate_2_Blue.pdf',
    isDirectory: false,
    size: 773862,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '8d6df1541a5b4c75a6cd4a4e1e47e061',
          name: 'GC_Intermediate_2_Blue.pdf',
          creator_account_id: '032595cf63f74799816efd8f35397247',
          content_type: 'application/pdf',
          size: 773862,
          state: 'pending',
          created: '08/15/2022',
          public: false,
          modified: '08/15/2022',
          age_in_folder: '2022-08-15T11:09:56-07:00',
          public_url: '',
        },
        security: [
          { permission: 'reader', user_id: '032595cf63f74799816efd8f35397247' },
          { permission: 'owner', user_id: '546456546y54hgg54h54h4fg73454g55' },
        ],
      },
      members: 'Only you',
    },
    dateModified: '08/15/2022',
    path: '',
    thumbnail: '',
    key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  },
};

export const mockDocumentFileWithTwoOwnerUsers: FileSystemItem = {
  name: 'GC_Intermediate_2_Blue.pdf',
  key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  path: 'GC_Intermediate_2_Blue.pdf',
  pathKeys: ['8d6df1541a5b4c75a6cd4a4e1e47e061'],
  isDirectory: false,
  size: 773862,
  dateModified: new Date('2022-08-14T22:00:00.000Z'),
  thumbnail: '',
  hasSubDirectories: false,
  getFileExtension: () => 'docx',
  dataItem: {
    name: 'GC_Intermediate_2_Blue.pdf',
    isDirectory: false,
    size: 773862,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '8d6df1541a5b4c75a6cd4a4e1e47e061',
          name: 'GC_Intermediate_2_Blue.pdf',
          creator_account_id: '032595cf63f74799816efd8f35397247',
          content_type: 'application/pdf',
          size: 773862,
          state: 'pending',
          created: '08/15/2022',
          public: false,
          modified: '08/15/2022',
          age_in_folder: '2022-08-15T11:09:56-07:00',
          public_url: '',
        },
        security: [
          { permission: 'owner', user_id: '032595cf63f74799816efd8f35397247' },
          { permission: 'owner', user_id: '546456546y54hgg54h54h4fg73454g55' },
        ],
      },
      members: 'Only you',
    },
    dateModified: '08/15/2022',
    path: '',
    thumbnail: '',
    key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  },
};

export const mockDocumentFileWithReaderPermission: FileSystemItem = {
  name: 'GC_Intermediate_2_Blue.pdf',
  key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  path: 'GC_Intermediate_2_Blue.pdf',
  pathKeys: ['8d6df1541a5b4c75a6cd4a4e1e47e061'],
  isDirectory: false,
  size: 773862,
  dateModified: new Date('2022-08-14T22:00:00.000Z'),
  hasSubDirectories: false,
  getFileExtension: () => 'docx',
  thumbnail: '',
  dataItem: {
    name: 'GC_Intermediate_2_Blue.pdf',
    isDirectory: false,
    size: 773862,
    dataItem: {
      item: {
        entity_kind: 'file_descriptor',
        entity: {
          entity_id: '8d6df1541a5b4c75a6cd4a4e1e47e061',
          name: 'GC_Intermediate_2_Blue.pdf',
          creator_account_id: '032595cf63f74799816efd8f35397247',
          content_type: 'application/pdf',
          size: 773862,
          state: 'pending',
          created: '08/15/2022',
          public: false,
          modified: '08/15/2022',
          age_in_folder: '2022-08-15T11:09:56-07:00',
          public_url: '',
        },
        security: [{ permission: FilePermissions.Reader, user_id: '032595cf63f74799816efd8f35397247' }],
      },
      members: 'Only you',
    },
    dateModified: '08/15/2022',
    path: '',
    thumbnail: '',
    key: '8d6df1541a5b4c75a6cd4a4e1e47e061',
  },
};

export const fileFromManager: FileFromManager = {
  component: null, // don't use
  element: null, // don't use
  file: {
    name: 'INT_WS_CO_08_09_Intermediate_W08___ONL_Student.pdf',
    key: '9cb2c824e52b4d2a87a38b5b7e4f815d',
    path: 'INT_WS_CO_08_09_Intermediate_W08___ONL_Student.pdf',
    pathKeys: ['9cb2c824e52b4d2a87a38b5b7e4f815d'],
    isDirectory: false,
    size: 1912087,
    dateModified: new Date('2022-11-11T22:00:00.000Z'),
    thumbnail: '',
    dataItem: {
      name: 'INT_WS_CO_08_09_Intermediate_W08___ONL_Student.pdf',
      isDirectory: false,
      size: 1912087,
      dataItem: {
        item: {
          entity_kind: 'file_descriptor',
          entity: {
            entity_id: '9cb2c824e52b4d2a87a38b5b7e4f815d',
            name: 'INT_WS_CO_08_09_Intermediate_W08___ONL_Student.pdf',
            creator_account_id: '50a2d93e803241df8dd64994364e3853',
            content_type: 'application/pdf',
            size: 1912087,
            state: 'pending',
            created: '11/12/2022',
            public: false,
            modified: '11/12/2022',
            age_in_folder: '2022-11-12T14:09:20-08:00',
            public_url: '',
          },
          security: [{ permission: 'owner', user_id: '50a2d93e803241df8dd64994364e3853' }],
        },
        members: 'Only you',
      },
      dateModified: '11/12/2022',
      hasSubDirectories: false,
      path: '',
      thumbnail: '',
      key: '9cb2c824e52b4d2a87a38b5b7e4f815d',
    },
  } as FileSystemItem,
};
